import React, { useEffect, useState } from "react";
import { Typography, Box, Chip, TextField, CircularProgress, MenuItem } from "@mui/material";
import {
  LocalShipping,
  Science,
  Numbers,
  WaterDrop,
  CalendarMonth,
  Category,
  Badge,
  Comment as CommentIcon,
} from "@mui/icons-material";

const toDateInputValue = (date) => (date ? date.split("T")[0] : "");

export const SAMPLE_TYPE_OPTIONS = ["Grab", "Composite", "Other"];

// Renders one field as a small always-editable input (click in, edit, tab/blur out to save)
// instead of read-only text — mirrors the quick-edit feel of the legacy VLP grid.
// Fields with `options` render as a dropdown that saves as soon as a value is picked.
const EditableField = ({ recordid, field, initialValue, type, options, onFieldSave }) => {
  const [value, setValue] = useState(initialValue ?? "");
  const [saving, setSaving] = useState(false);

  // Re-sync when a different bottle is shown or the underlying data changes elsewhere
  useEffect(() => {
    setValue(initialValue ?? "");
  }, [recordid, initialValue]);

  if (options) {
    // Legacy char(n) columns can carry trailing spaces, which would not match any option
    const current = (value ?? "").toString().trim();
    // Keep a value that is no longer in the list visible instead of rendering a blank field
    const choices = current && !options.includes(current) ? [current, ...options] : options;

    const handleSelect = async (e) => {
      const selected = e.target.value;
      if (selected === current) return;

      setValue(selected);
      setSaving(true);
      try {
        await onFieldSave(field, selected);
      } catch (error) {
        setValue(initialValue ?? "");
      } finally {
        setSaving(false);
      }
    };

    return (
      <TextField
        select
        variant="standard"
        size="small"
        fullWidth
        value={current}
        onChange={handleSelect}
        disabled={saving}
        InputProps={{ sx: { fontSize: "0.8rem" } }}
        SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
      >
        <MenuItem value="" sx={{ fontSize: "0.8rem" }}>
          <em>None</em>
        </MenuItem>
        {choices.map((option) => (
          <MenuItem key={option} value={option} sx={{ fontSize: "0.8rem" }}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  const handleBlur = async () => {
    const isEmptyDate = type === "date" && value === "";
    const normalized = type === "number" ? Number(value) || 0 : isEmptyDate ? null : value;
    const previous = initialValue ?? (type === "number" ? 0 : "");
    if (normalized === previous || (isEmptyDate && previous === "")) return;

    setSaving(true);
    try {
      await onFieldSave(field, normalized);
    } catch (error) {
      setValue(initialValue ?? "");
    } finally {
      setSaving(false);
    }
  };

  return (
    <TextField
      variant="standard"
      size="small"
      fullWidth
      type={type === "number" ? "number" : type === "date" ? "date" : "text"}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
      disabled={saving}
      InputProps={{
        sx: { fontSize: "0.8rem" },
        endAdornment: saving ? <CircularProgress size={12} /> : null,
      }}
    />
  );
};

const ViewOrderedBottle = ({ orderedBottleData, onFieldSave, sampleBottles = [] }) => {
  if (!orderedBottleData) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No ordered bottle data available
        </Typography>
      </Box>
    );
  }

  // Data-driven fields = every cell renders through the same markup,
  // so labels/values line up in a real grid instead of drifting per-field.
  // Each field with a `field` key renders as an editable input that saves on blur.
  const sampleBottleOptions = [
    ...new Set(sampleBottles.map((b) => b.sampleBottleName?.trim()).filter(Boolean)),
  ];

  const fields = [
    { label: "CofC Description", field: "cocNum", value: orderedBottleData.cocNum, icon: Badge },
    { label: "Sample Bottle Name", field: "sampleBottleName", value: orderedBottleData.sampleBottleName, icon: Science, options: sampleBottleOptions },
    { label: "Sample Type", field: "sampleType", value: orderedBottleData.sampleType, icon: Category, options: SAMPLE_TYPE_OPTIONS },
    { label: "Quantity", field: "quantity", value: orderedBottleData.quantity, icon: Numbers, type: "number" },
    { label: "Parameter", field: "parameter", value: orderedBottleData.parameter, icon: Science },
    { label: "Preservatives", field: "preservatives", value: orderedBottleData.preservatives, icon: Science },
    { label: "Water System Facility Name", field: "waterSystemFacilityName", value: orderedBottleData.waterSystemFacilityName, icon: WaterDrop },
    { label: "Water Facility ID", field: "waterFacilityId", value: orderedBottleData.waterFacilityId, icon: Badge },
    { label: "Monitor Start", field: "monitorStart", value: toDateInputValue(orderedBottleData.monitorStart), icon: CalendarMonth, type: "date" },
    { label: "Monitor End", field: "monitorEnd", value: toDateInputValue(orderedBottleData.monitorEnd), icon: CalendarMonth, type: "date" },
    { label: "Sample Frequency", field: "sampleFrequency", value: orderedBottleData.sampleFrequency, icon: CalendarMonth },
    { label: "Additional Notes", field: "comment", value: orderedBottleData.comment, icon: CommentIcon, wide: true },
  ];

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: "background.default",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      {/* Compact header - one line */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <LocalShipping sx={{ color: "primary.main", fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Ordered Bottle
        </Typography>
        <Chip
          label={`#${orderedBottleData.orderNumber || "N/A"}`}
          color="primary"
          size="small"
          sx={{ height: 18, fontSize: "0.7rem", fontWeight: 600 }}
        />
        {orderedBottleData.identifier && (
          <Typography variant="caption" color="text.secondary">
            Identifier: {orderedBottleData.identifier}
          </Typography>
        )}
      </Box>

      {/* Fixed-width columns via CSS grid keep labels/values aligned
          regardless of how long any individual value is. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          columnGap: 2,
          rowGap: 1,
        }}
      >
        {fields.map(({ label, field, value, icon: Icon, wide, type, options }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              gridColumn: wide ? "1 / -1" : undefined,
            }}
          >
            {Icon && (
              <Icon
                sx={{
                  color: "primary.main",
                  fontSize: 14,
                  mt: "2px",
                  opacity: 0.6,
                  flexShrink: 0,
                }}
              />
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.2, fontSize: "0.7rem" }}
              >
                {label}
              </Typography>
              {onFieldSave ? (
                <EditableField
                  recordid={orderedBottleData.recordid}
                  field={field}
                  initialValue={value}
                  type={type}
                  options={options}
                  onFieldSave={(f, v) => onFieldSave(orderedBottleData, f, v)}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{ wordBreak: "break-word", fontSize: "0.8rem", lineHeight: 1.3 }}
                >
                  {value || "N/A"}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ViewOrderedBottle;
